import { Country } from 'library/ts/model/enums/geo/Country';
import { IParamsAd } from 'tag/ts/tag/interfaces/params/IParamsAd';
import { IParamsContent } from 'tag/ts/tag/interfaces/params/IParamsContent';
import { IParamsContext } from 'tag/ts/tag/interfaces/params/IParamsContext';
import { IParamsSkin } from 'tag/ts/tag/interfaces/params/IParamsSkin';
import { IParamsSystem } from 'tag/ts/tag/interfaces/params/IParamsSystem';
import { IParamsTag } from 'tag/ts/tag/interfaces/params/IParamsTag';
import { IParamsUser } from 'tag/ts/tag/interfaces/params/IParamsUser';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { EmbedContext } from 'tag/ts/tag/model/enums/EmbedContext';
import { EmbedDevice } from 'tag/ts/tag/model/type/embed/EmbedDevice';
import { EmbedDeviceVersion } from 'tag/ts/tag/model/type/embed/EmbedDeviceVersion';
import { EmbedPlatform } from 'tag/ts/tag/model/type/embed/EmbedPlatform';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag parameters
 */
export class ParamsVO implements IParamsVO {
	/**
	 * @description Embed system related parameters
	 * @type {IParamsSystem}
	 */
	SYSTEM: IParamsSystem = {
		// Embed or application Type smart tag is embedded by
		embedType: EmbedType.DESKTOP,
		// platform smart tag is embedded by
		platform: EmbedPlatform.DESKTOP,
		// device smart tag is embedded by
		device: EmbedDevice.DESKTOP,
		// device version smart tag is embedded by
		deviceVersion: EmbedDeviceVersion.DESKTOP[ '2017' ]
	};

	/**
	 * @description Tag related parameters
	 * @type {IParamsTag}
	 */
	TAG: IParamsTag = {
		uuid: undefined,
		embedContext: EmbedContext.INLINE,
		callback: undefined,
		config: {
			tag: undefined,
			skin: undefined
		},
		container: undefined,
		// define default background color on Smart Tag skeleton DOM elements
		background: {
			smartTag: {
				color: 'black'
			},
			content: {
				color: 'black'
			},
			overlay: {
				color: 'black'
			}
		},
		namespace: undefined,
		playerOptions: undefined,
		// enable Tag Error Panel: default: true
		errorPanel: true,
		// enable Tag Loading Animation overlay: default: true
		loadingAnimation: true,
		// loading animation config
		loadingAnimationConfig: {
			timeoutForMessage: 5000,
			timeoutMessage: 'Optimizing your video playback experience',
			backgroundImage: {
				enabled: false,
				imageURL: undefined,
				overlayOpacity: 0.6
			},
			spinner: {
				enabled: true
			},
			fadeIn: {
				enabled: false,
				duration: 300
			}
		},

		loadingThrottleEnabled: true,
		loadingThrottleMS: 50,

		// enable Tag platform support check: default: true (on desktop we must check supported browser versions)
		platformSupportCheck: true,
		trackingDisabled: false,

		// govern whether to prefer legacy tracking over DIT DataHub tracking
		useLegacyTracking: false,

		// guard tag.API.VIDEO.load() against a missing/invalid JWT on Stitcher content (PLBCK-8587)
		invalidJwtLoadGuard: false
	};
	/**
	 * @description Ad related parameters
	 * @type {IParamsAd}
	 */
	AD: IParamsAd = {
		AB: {
			method: undefined,
			interval: 5000,
			delay: 7000
		},
		IMA: {
			iu: undefined
		},
		SETTINGS: undefined,
		PAUSE: undefined,
		SDK: {
			// By default, we ask Smart Tag to preload Ad SDK instead of avia ad adapter plugin
			preload: true
		},
		LINEAR_AD_TRACKING: undefined,
		CONTEXT: undefined,
		vodf: false
	};
	/**
	 * @description Content related parameters
	 * @type {IParamsContent}
	 */
	CONTENT: IParamsContent = {
		apiMetadata: undefined,
		millstone: undefined,
		assetURL: undefined,
		autoplay: false,
		brandLogoUrl: undefined,
		contentId: undefined,
		country: Country.US,
		drm: undefined,
		globalResumeTime: NaN,
		isLive: false,
		locale: 'en-us',
		partner: 'pplus',
		pid: undefined,
		resource: undefined,
		// Smart Tag DAI failover is enabled for Desktop & HTML5 by default
		daiFailover: [ EmbedType.DESKTOP, EmbedType.HTML5 ],
		loadingAnimationBackgroundImageURL: undefined
	};
	/**
	 * @description Skin application related parameters
	 * @type {IParamsSkin}
	 */
	SKIN: IParamsSkin = {
		// Host may omit; TagUtilities.normalizeParams sets { enabled: true } on merged params when unset.
		cp: undefined,
		fch: undefined,
		enabled: true,
		defaultSkin: false,
		markersVisible: true,
		liveLogoURL: undefined,
		liveStreamTimeout: NaN,
		liveStreamTimeoutCountdown: NaN,
		liveCountdownTemplate: undefined,
		vodTimeoutPlaybackTimeoutEpisode: NaN,
		vodTimeoutPlaybackTimeoutMovie: NaN,
		vodTimeoutInactivityTimeoutPrompt: NaN,
		playbackEvents: undefined,
		onStart: {
			ccVisible: undefined,
			controlsVisible: undefined,
			mute: undefined
		},
		brandLogoUrl: undefined
	};
	/**
	 * @description User related parameters
	 * @type {IParamsUser}
	 */
	USER: IParamsUser = {
		userId: undefined,
		profileId: undefined,
		userStatus: 'anon',
		bundleStatus: '',
		pinGate: false,
		mvpdId: undefined,
		usPrivacy: undefined,
		packageSource: undefined,
		settings: {
			disablePremiumVideo: false
		}
	};
	/**
	 * @description Content context related parameters
	 * @type {IParamsContext}
	 */
	CONTEXT: IParamsContext = {
		metadata: undefined
	};
}